import React, { useEffect, useRef, useState } from 'react';
import { Play, Download, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../context/LangContext';
import { useEditor } from '../context/EditorContext';
import { stats } from '../mock/data';

const Hero = () => {
  const { t, lang } = useLang();
  const { openEditor } = useEditor();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMouse({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section ref={heroRef} className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      {/* Soft ambient blobs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 -left-40 w-[520px] h-[520px] bg-cyan-200/40 dark:bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute top-40 right-0 w-[480px] h-[480px] bg-pink-200/40 dark:bg-fuchsia-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/2 w-[420px] h-[420px] bg-yellow-100/50 dark:bg-amber-500/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 shadow-sm rounded-full px-4 py-1.5 mb-8 animate-fade-up">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-[13px] font-semibold text-neutral-800 dark:text-neutral-100 tracking-tight">{t.hero.badge}</span>
          </div>

          <h1 className="font-black tracking-[-0.03em] text-neutral-900 dark:text-white leading-[0.95] text-[52px] sm:text-[68px] md:text-[88px] lg:text-[104px]">
            {t.hero.title}
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-cyan-500 via-blue-500 to-fuchsia-500 bg-clip-text text-transparent">
                {t.hero.titleAccent}
              </span>
              <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none">
                <path d="M2 8 Q 75 2, 150 8 T 298 8" stroke="url(#g1)" strokeWidth="4" strokeLinecap="round" fill="none" />
                <defs>
                  <linearGradient id="g1" x1="0" x2="300" y1="0" y2="0">
                    <stop offset="0" stopColor="#06b6d4" />
                    <stop offset="0.5" stopColor="#3b82f6" />
                    <stop offset="1" stopColor="#d946ef" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h1>

          <p className="mt-8 text-[17px] md:text-[19px] leading-relaxed text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={openEditor} className="h-14 px-8 rounded-full bg-black dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-200 text-white dark:text-black font-semibold text-[15px] shadow-lg shadow-black/10 group">
              <Play className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2 fill-current" />
              {t.hero.ctaPrimary}
              <ArrowRight className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2 rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" className="h-14 px-8 rounded-full border-2 border-neutral-200 dark:border-white/15 hover:border-neutral-900 dark:hover:border-white bg-transparent hover:bg-transparent text-neutral-900 dark:text-white font-semibold text-[15px]">
              <Download className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {t.hero.ctaSecondary}
            </Button>
          </div>

          <p className="mt-6 text-[13px] text-neutral-500 dark:text-neutral-400 flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {t.hero.trust}
          </p>
        </div>

        {/* Editor mockup preview */}
        <div
          className="relative mt-16 md:mt-20 mx-auto max-w-5xl"
          style={{ transform: `perspective(1200px) rotateX(${8 - mouse.y * 0.2}deg) rotateY(${mouse.x * 0.2}deg)` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/30 via-blue-400/20 to-fuchsia-400/30 blur-3xl scale-95" />
          <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-neutral-200 shadow-2xl shadow-neutral-900/20 bg-neutral-900">
            {/* window chrome */}
            <div className="flex items-center gap-1.5 px-4 py-3 bg-neutral-900 border-b border-neutral-800">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <div className="flex-1 text-center text-[11px] text-neutral-500 font-medium">CapCute Editor — {lang === 'sv' ? 'Nytt projekt' : 'مشروع جديد'}</div>
            </div>
            {/* editor body */}
            <div className="grid grid-cols-12 gap-0 min-h-[380px] md:min-h-[460px]">
              <aside className="col-span-2 bg-neutral-950 border-r border-neutral-800 p-2 flex flex-col gap-1">
                {['Media','AI','Text','Effekt','Ljud','Mall'].map((n, i) => (
                  <div key={i} className={`aspect-square rounded-lg flex items-center justify-center text-[10px] font-semibold ${i===1 ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white':'bg-neutral-900 text-neutral-400 hover:bg-neutral-800'}`}>{n}</div>
                ))}
              </aside>
              <div className="col-span-7 bg-neutral-900 flex items-center justify-center p-4 relative">
                <div className="absolute inset-4 rounded-lg overflow-hidden bg-black">
                  <img src="https://images.unsplash.com/photo-1614963326505-843868e1d83a?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85" alt="preview" className="w-full h-full object-cover opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-3 left-3 rtl:left-auto rtl:right-3 bg-black/50 backdrop-blur px-2 py-1 rounded text-white text-[10px] font-medium">4K · 60fps</div>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                    <Play className="w-6 h-6 fill-black text-black ml-0.5" />
                  </div>
                </div>
              </div>
              <aside className="col-span-3 bg-neutral-950 border-l border-neutral-800 p-3 space-y-2">
                <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">AI Tools</div>
                {['Auto Captions','Bg Remover','Voice Clone','Color AI'].map((n, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-neutral-900 hover:bg-neutral-800">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-fuchsia-500 to-pink-500" />
                    <div className="text-[11px] text-neutral-300 font-medium">{n}</div>
                  </div>
                ))}
              </aside>
            </div>
            {/* timeline */}
            <div className="bg-neutral-950 border-t border-neutral-800 p-3 space-y-1.5">
              {[['bg-cyan-500', 'w-[65%]'], ['bg-fuchsia-500','w-[45%] ml-[10%]'], ['bg-amber-500','w-[80%]']].map(([c,w], i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="text-[9px] text-neutral-500 w-8">V{i+1}</div>
                  <div className="flex-1 h-6 bg-neutral-900 rounded overflow-hidden">
                    <div className={`h-full rounded ${c} ${w} opacity-80`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl md:text-5xl font-black tracking-tight text-neutral-900 dark:text-white">{s.value}</div>
              <div className="mt-1 text-[13px] text-neutral-500 dark:text-neutral-400 font-medium">{lang === 'sv' ? s.labelSv : s.labelAr}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
